/**
 * CollapsedLeftNav - HubSpot-style collapsed sidebar
 * 60px wide, dark theme, icon-based navigation
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sun, Image, Mountain, Layers, ChevronsRight, ChevronsLeft } from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}

export default function CollapsedLeftNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const navItems: NavItem[] = [
    {
      id: 'solar-assessment',
      label: 'Solar Assessment',
      icon: <Sun className="h-6 w-6" />,
      path: '/assessment-v2',
    },
    {
      id: 'batches',
      label: 'Batch Management',
      icon: <Layers className="h-6 w-6" />,
      path: '/batches',
    },
    {
      id: 'geotiff-playground',
      label: 'GeoTIFF Playground',
      icon: <Image className="h-6 w-6" />,
      path: '/geotiff-playground-gmaps',
    },
    {
      id: 'dsm-3d-viewer',
      label: 'DSM 3D Viewer',
      icon: <Mountain className="h-6 w-6" />,
      path: '/dsm-3d-viewer',
    },
  ];

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path);
  };

  return (
    <nav
      className={`${isExpanded ? 'w-[240px]' : 'w-[60px]'} h-screen bg-white border-r-2 border-gray-200 flex flex-col py-4 z-50 overflow-visible transition-all duration-300 ease-in-out`}
      style={{ alignItems: isExpanded ? 'stretch' : 'center' }}
    >
      {/* Niko Logo - Top (Smaller) */}
      <div
        className={`mb-8 cursor-pointer hover:opacity-80 transition-opacity ${isExpanded ? 'px-4' : ''}`}
        onClick={() => navigate('/assessment-v2')}
      >
        {isExpanded ? (
          <img
            src="/LOGO NIKO NEGRO.svg"
            alt="Niko Energy"
            className="w-full h-auto object-contain"
          />
        ) : (
          <img
            src="/Niko-Logo-No-bkground.png"
            alt="Niko Energy"
            className="h-8 w-8 object-contain mx-auto"
          />
        )}
      </div>

      {/* Navigation Items - Middle */}
      <div className={`flex-1 flex flex-col gap-2 ${isExpanded ? 'px-3' : ''}`}>
        {navItems.map((item) => (
          <div key={item.id} className="relative">
            {/* Nav Button - Expands to show label when menu is expanded */}
            <button
              onClick={() => navigate(item.path)}
              onMouseEnter={() => !isExpanded && setHoveredItem(item.id)}
              onMouseLeave={() => setHoveredItem(null)}
              className={`
                ${isExpanded ? 'w-full px-4 py-3 justify-start gap-3' : 'w-12 h-12 justify-center'}
                rounded-lg flex items-center
                transition-all duration-200
                ${
                  isActive(item.path)
                    ? 'bg-[#EBFD03] text-black font-semibold'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }
              `}
              title={!isExpanded ? item.label : undefined}
            >
              {item.icon}
              {isExpanded && (
                <span className="text-sm font-medium">{item.label}</span>
              )}
            </button>

            {/* Hover Popover - Only show when collapsed */}
            {!isExpanded && hoveredItem === item.id && !isActive(item.path) && (
              <div className="absolute left-full ml-3 top-1/2 -translate-y-1/2 z-[9999] pointer-events-none">
                <div className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap shadow-xl relative">
                  {item.label}
                  {/* Arrow */}
                  <div className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8 border-r-gray-900" />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Expander Button - Bottom (No border) */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`
          ${isExpanded ? 'w-full px-4 py-3 justify-start gap-3' : 'w-12 h-12 justify-center'}
          rounded-lg flex items-center text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-all duration-200
          ${isExpanded ? 'mx-3' : ''}
        `}
        title={isExpanded ? 'Collapse menu' : 'Expand menu'}
      >
        {isExpanded ? (
          <>
            <ChevronsLeft className="h-5 w-5" />
            <span className="text-sm font-medium">Collapse</span>
          </>
        ) : (
          <ChevronsRight className="h-5 w-5" />
        )}
      </button>
    </nav>
  );
}
